import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ErrorReceived } from './error.actions';
import { combineLatest } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthSelectors } from '@cpd-app/state/auth';
import { CognitoAuthSelectors } from '@cpd-app/state/cognito-auth';

/**
 * An authorization failure is not a fault the member can act on.
 *
 * Several effects funnel every failure here as `ErrorReceived`, and the
 * occupation pages ask for `occupation` and `getFeatureFlags`, both of which
 * answer "Forbidden resource" to somebody who is not signed in. That put a red
 * "An error occurred" bar over a page that had rendered correctly and was
 * already saying, in its own words, what signing in would add.
 *
 * The same rule the GraphQL interceptors follow: a Forbidden is a statement
 * about permission, not a broken server, so it does not get a server-error
 * message. Everything else still surfaces, including a genuine failure while
 * signed in.
 *
 * This only describes the SHAPE of the failure. Whether to suppress it is
 * decided by the caller, which also asks whether there is a session: a member
 * who IS signed in and gets a 401 has had their session expire, and telling
 * them nothing is how main's message went missing.
 */
const isAuthorizationError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as {
    status?: number;
    message?: string;
    networkError?: { status?: number };
    graphQLErrors?: { extensions?: { code?: string }; message?: string }[];
    // The gateway's own shape, which the session interceptor reads as
    // `response.error.errors` and which reaches here unchanged from callers
    // that pass a whole failed response rather than an ApolloError.
    errors?: { extensions?: { code?: string }; message?: string }[];
  };

  const status = candidate.status ?? candidate.networkError?.status;
  if (status === 401 || status === 403) {
    return true;
  }

  const reported = [
    ...(candidate.graphQLErrors ?? []),
    ...(candidate.errors ?? [])
  ];

  if (
    reported.some(
      (entry) =>
        entry?.extensions?.code === 'FORBIDDEN' ||
        entry?.extensions?.code === 'UNAUTHENTICATED' ||
        /forbidden|unauthenticated|unauthorized/i.test(entry?.message ?? '')
    )
  ) {
    return true;
  }

  /*
   * The message, last. Apollo does not always keep `graphQLErrors` by the time
   * an effect's catchError sees the failure: the gateway's wording arrives as a
   * plain message, which is how this reached the page in the first place.
   */
  /*
   * "Session expired - no refresh token" belongs here too, and it is what a
   * signed-out visitor was actually being shown. The interceptor raises it
   * when a refresh is attempted with no refresh token in storage, which is the
   * normal state for somebody who has never signed in, not a fault they can do
   * anything about. It reached the page as a red bar reading "An error
   * occurred", because the wording was being flattened before it got here.
   */
  return /forbidden|unauthenticated|unauthorized|session expired|no refresh token/i.test(
    candidate.message ?? ''
  );
};

@Injectable()
export class ErrorEffects {
  /*
   * Read into a field rather than combined into the stream, so the effect
   * keeps working when no store is supplied (the unit tests build this class
   * directly). No session is the safe default: that is the case the
   * suppression above was written for.
   */
  private isAuthenticated = false;

  constructor(
    private actions$: Actions,
    private snackBar: MatSnackBar,
    private store?: Store
  ) {
    /*
     * BOTH sessions count as signed in.
     *
     * `cognitoAuth.isAuthenticated` is the member's flag and
     * `auth.isAuthenticated` is the counselor and admin one:
     * `deriveRestoredAuthorization` only ever sets the second for a
     * counselor, admin or super admin, so a learner's session never turns it
     * true. Reading one of the two would have left the other half of the user
     * base silenced, which is the defect being fixed.
     */
    if (this.store) {
      combineLatest([
        this.store.select(CognitoAuthSelectors.selectIsAuthenticated),
        this.store.select(AuthSelectors.selectIsAuthenticated)
      ]).subscribe(([isMember, isCounselor]) => {
        this.isAuthenticated = !!isMember || !!isCounselor;
      });
    }
  }

  errorMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ErrorReceived),
        /*
         * Suppressed only while there is no session. Signed in, a 401 or a 403
         * means the session died under the member, and main told them so.
         */
        filter(
          ({ error }) => this.isAuthenticated || !isAuthorizationError(error)
        ),
        tap(({ error }) => {
          /*
           * Optional chaining, because this effect is the app's error reporter
           * and an exception thrown in here terminates the stream: after that
           * NO further error is ever reported for the rest of the session.
           * `ErrorReceived({ error })` is dispatched from a dozen catchError
           * handlers, and a failure with no error object at all reached
           * `error.message` and killed it.
           */
          /*
           * The generic fallback STAYS. Suppressing a toast that has no message
           * was tried here and taken back out: a real outage arrives as a 500
           * with nothing readable in it, and this bar is the only thing that
           * tells the member the page is not simply empty. The fix for the
           * bare "An error occurred" belongs upstream, where the wording was
           * being discarded, not here where it is the last resort.
           */
          this.snackBar.open(error?.message || 'An error occurred', 'Close', {
            duration: 10 * 1000,
            panelClass: 'error-snackbar'
          });
        })
      ),
    { dispatch: false }
  );
}
